// src/components/kpi/KpiFillModal.jsx
import React, { useEffect, useMemo, useState } from "react";
import { Modal, Form } from "react-bootstrap";
import "./KpiModal.css";

function kindNorm(kind) {
  return String(kind || "").trim().toLowerCase();
}

function unitLabel(kind) {
  const k = kindNorm(kind);
  if (k === "pct") return "%";
  if (k === "days") return "Days";
  if (k === "hours") return "Hours";
  if (k === "score") return "Score";
  if (k === "int") return "#";
  return "#";
}

function placeholderFor(kind) {
  const k = kindNorm(kind);
  if (k === "pct") return "0-100";
  if (k === "days") return "Days";
  if (k === "hours") return "Hours";
  if (k === "score") return "0-100";
  return "";
}

function inputModeFor(kind) {
  const k = kindNorm(kind);
  if (k === "pct" || k === "score") return "decimal";
  return "numeric";
}

function sanitize(kind, raw) {
  const s = String(raw ?? "");
  if (s.trim() === "") return "";

  const k = kindNorm(kind);

  // int/days/hours: digits only
  if (k === "int" || k === "days" || k === "hours") {
    return s.replace(/[^\d]/g, "");
  }

  // pct/score/decimal: digits + single dot
  const cleaned = s.replace(/[^\d.]/g, "");
  const parts = cleaned.split(".");
  if (parts.length <= 1) return cleaned;
  return `${parts[0]}.${parts.slice(1).join("")}`;
}

function toInputStr(kind, v) {
  if (v === null || v === undefined) return "";
  const s = String(v).trim();
  if (s === "" || s === "null") return "";

  const n = Number(s);
  if (!Number.isFinite(n)) return "";

  const k = kindNorm(kind);

  if (k === "int" || k === "days" || k === "hours") return String(parseInt(n, 10));
  if (k === "pct") return String(parseFloat(n.toFixed(2)));
  if (k === "score") return String(parseFloat(n.toFixed(0)));
  return String(parseFloat(n.toFixed(4)));
}

function validate(kind, raw) {
  const k = kindNorm(kind);
  if (raw === "" || raw === null || raw === undefined) return null; // empty ok

  const n = Number(raw);
  if (!Number.isFinite(n)) return "Invalid number";

  if (k === "int" || k === "days" || k === "hours") {
    if (!Number.isInteger(n)) return "Must be an integer";
    if ((k === "days" || k === "hours") && n < 0) return "Must be >= 0";
  }

  if (k === "pct") {
    if (n < 0 || n > 100) return "Must be between 0 and 100";
  }

  if (k === "score") {
    if (n < 0 || n > 100) return "Must be between 0 and 100";
  }

  return null;
}

export default function KpiFillModal({ show, onHide, period, country, onSave, onToast }) {
  const entity = String(country ?? "").trim().toUpperCase(); // keep prop name for compatibility

  const [defs, setDefs] = useState([]);
  const [values, setValues] = useState({});
  const [comment, setComment] = useState("");
  const [saving, setSaving] = useState(false);

  const groupedDefs = useMemo(() => {
    const map = new Map();
    for (const d of defs) {
      const area = String(d.board_area || d.category || "KPIs");
      if (!map.has(area)) map.set(area, []);
      map.get(area).push(d);
    }
    const out = Array.from(map.entries()).sort((a, b) => String(a[0]).localeCompare(String(b[0])));
    for (const [, arr] of out) {
      arr.sort(
        (a, b) =>
          Number(a.board_sort ?? a.sort_order ?? 0) - Number(b.board_sort ?? b.sort_order ?? 0)
      );
    }
    return out;
  }, [defs]);

  useEffect(() => {
    if (!show) return;

    setDefs([]);
    setValues({});
    setComment("");
    setSaving(false);

    (async () => {
      try {
        const url = `/api/kpi.php?entity=${encodeURIComponent(entity)}&period=${encodeURIComponent(period)}`;
        const res = await fetch(url, { credentials: "include" });
        const data = await res.json().catch(() => null);
        if (!data?.success || !Array.isArray(data.data)) return;

        const nextDefs = data.data.map((r) => ({
          kpi_key: r.kpi_key,
          label: r.label,
          category: r.category,
          value_kind: r.value_kind,
          board_area: r.board_area,
          board_kpi_label: r.board_kpi_label,
          board_sort: r.board_sort,
          sort_order: r.sort_order,
          value: r.value,
          comment: r.comment,
        }));

        const nextValues = {};
        let cmt = "";
        for (const r of nextDefs) {
          nextValues[r.kpi_key] = toInputStr(r.value_kind, r.value);
          if (!cmt && r.comment) cmt = String(r.comment);
        }

        setDefs(nextDefs);
        setValues(nextValues);
        setComment(cmt || "");
      } catch (e) {
        console.error(e);
      }
    })();
  }, [show, entity, period]);

  const updateValue = (key, kind, v) => {
    const sanitized = sanitize(kind, v);
    setValues((p) => ({ ...p, [key]: sanitized }));
  };

  const handleSave = async () => {
    for (const d of defs) {
      const raw = values[d.kpi_key];
      const err = validate(d.value_kind, raw);
      if (err) {
        const label = d.board_kpi_label || d.label || d.kpi_key;
        return onToast?.(`${label}: ${err}`, "warning");
      }
    }

    const payload = {
      period,
      entity,
      country: entity, // backward compatibility
      comment: comment?.trim() || null,
      kpis: {},
    };

    for (const d of defs) {
      const raw = values[d.kpi_key];
      if (raw === "" || raw === null || raw === undefined) continue;
      payload.kpis[d.kpi_key] = Number(raw);
    }

    try {
      setSaving(true);
      await onSave?.(payload);
      onHide?.();
    } catch (e) {
      console.error(e);
      onToast?.("Save failed", "danger");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Modal
      show={show}
      onHide={saving ? undefined : onHide}
      centered
      size="lg"
      backdrop="static"
      dialogClassName="kpi-modal"
      contentClassName="kpi-modal-content"
    >
      <Modal.Header closeButton={!saving} className="kpi-modal-header">
        <Modal.Title className="kpi-modal-title">Add KPIs</Modal.Title>
      </Modal.Header>

      <Modal.Body className="kpi-modal-body">
        <div className="kpi-modal-meta">
          Period: <strong>{period}</strong> | Entity: <strong>{entity}</strong>
        </div>

        {!defs.length ? (
          <div className="kpi-warning">No KPI definitions found.</div>
        ) : (
          <div className="kpi-form-grid">
            {groupedDefs.flatMap(([area, items]) => [
              <div key={`hdr-${area}`} className="kpi-form-row kpi-form-row-full">
                <div style={{ fontWeight: 700, paddingTop: 10 }}>{area}</div>
              </div>,
              ...items.map((kpi) => {
                const label = kpi.board_kpi_label || kpi.label || kpi.kpi_key;
                const kind = kpi.value_kind;

                return (
                  <div className="kpi-form-row" key={kpi.kpi_key}>
                    <label className="kpi-form-label">{label}</label>

                    <div className="kpi-input-wrap" style={{ display: "flex", gap: 8, alignItems: "center" }}>
                      <input
                        type="text"
                        inputMode={inputModeFor(kind)}
                        className="kpi-input"
                        value={values[kpi.kpi_key] ?? ""}
                        onChange={(e) => updateValue(kpi.kpi_key, kind, e.target.value)}
                        disabled={saving}
                        placeholder={placeholderFor(kind)}
                        aria-label={label}
                      />
                      <span className="kpi-unit" title={`Unit: ${unitLabel(kind)}`}>
                        {unitLabel(kind)}
                      </span>
                    </div>
                  </div>
                );
              }),
            ])}

            <div className="kpi-form-row kpi-form-row-full">
              <Form.Group>
                <Form.Label className="kpi-form-label">Comment (optional)</Form.Label>
                <Form.Control
                  as="textarea"
                  rows={3}
                  className="kpi-textarea"
                  value={comment}
                  onChange={(e) => setComment(e.target.value)}
                  disabled={saving}
                />
              </Form.Group>
            </div>
          </div>
        )}
      </Modal.Body>

      <Modal.Footer className="kpi-modal-footer">
        <button type="button" className="kpi-btn" onClick={onHide} disabled={saving}>
          Cancel
        </button>
        <button type="button" className="kpi-btn kpi-btn-primary" onClick={handleSave} disabled={saving || !defs.length}>
          {saving ? "Saving..." : "Save"}
        </button>
      </Modal.Footer>
    </Modal>
  );
}